import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { sessionsAPI, timesAPI } from '../../services/api';
import part1Questions from '../../data/questions/part1.json';
import './QuizResults.css';

const allQuestionsMap = {};
[part1Questions].forEach((partData, idx) => {
  partData.questions.forEach(q => {
    allQuestionsMap[q.id] = { ...q, partNumber: idx + 1 };
  });
});

function QuizResults() {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState({});
  const [times, setTimes] = useState({});
  const [filter, setFilter] = useState('all');
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadResults() {
      try {
        const sessionData = await sessionsAPI.get(sessionId);
        setSession(sessionData.session);

        const storedAnswers = localStorage.getItem(`quiz_answers_${sessionId}`);
        if (storedAnswers) {
          setAnswers(JSON.parse(storedAnswers));
        }

        const timesData = await timesAPI.getAll();
        setTimes(timesData.times || {});
      } catch (err) {
        console.error('Failed to load results:', err);
        setError('Failed to load quiz results');
      } finally {
        setIsLoading(false);
      }
    }

    loadResults();
  }, [sessionId]);

  if (isLoading) {
    return <div className="loading">Loading results...</div>;
  }

  if (error || !session) {
    return (
      <div className="quiz-results">
        <div className="error-message">{error || 'Session not found'}</div>
        <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
          Return to Dashboard
        </button>
      </div>
    );
  }

  const questionIds = session.questionIds;
  const questions = questionIds.map(id => allQuestionsMap[id]).filter(Boolean);

  let correctCount = 0;
  let totalTimeSeconds = 0;
  const questionResults = questions.map(q => {
    const answer = answers[q.id];
    const isCorrect = answer !== undefined && answer === q.correctAnswer;
    if (isCorrect) correctCount++;
    const time = times[q.id] || 0;
    totalTimeSeconds += time;
    return {
      question: q,
      userAnswer: answer,
      isCorrect,
      time
    };
  });

  const percentage = Math.round((correctCount / questions.length) * 100);

  const filteredResults = questionResults.filter(r => {
    if (filter === 'incorrect') return !r.isCorrect;
    if (filter === 'correct') return r.isCorrect;
    return true;
  });

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const optionLetters = ['A', 'B', 'C', 'D'];

  const handleRetryQuiz = async () => {
    try {
      const result = await sessionsAPI.create(
        session.mode,
        questionIds,
        session.time_limit_seconds
      );
      localStorage.removeItem(`quiz_answers_${sessionId}`);
      navigate(`/quiz/${result.sessionId}`);
    } catch (err) {
      console.error('Failed to create new session:', err);
      alert('Failed to start new quiz');
    }
  };

  return (
    <div className="quiz-results">
      <div className="results-header">
        <h1>Quiz Complete</h1>
      </div>

      <div className="results-summary">
        <div className="score-circle">
          <div className="score-value">{percentage}%</div>
          <div className="score-fraction">{correctCount}/{questions.length}</div>
        </div>
        <div className="score-bar">
          <div className="score-bar-fill" style={{ width: `${percentage}%` }} />
        </div>
        {totalTimeSeconds > 0 && (
          <div className="time-summary">
            Total time: {formatTime(totalTimeSeconds)}
          </div>
        )}
        <div className="score-message">
          {percentage >= 80 ? 'Excellent work!' :
           percentage >= 60 ? 'Good effort. Review the missed questions.' :
           'Consider reviewing the material and trying again.'}
        </div>
      </div>

      <div className="results-filter">
        <span>Review:</span>
        <button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({questions.length})
        </button>
        <button
          className={`filter-btn ${filter === 'incorrect' ? 'active' : ''}`}
          onClick={() => setFilter('incorrect')}
        >
          Incorrect ({questions.length - correctCount})
        </button>
        <button
          className={`filter-btn ${filter === 'correct' ? 'active' : ''}`}
          onClick={() => setFilter('correct')}
        >
          Correct ({correctCount})
        </button>
      </div>

      <div className="results-grid">
        {filteredResults.map((result, idx) => (
          <div
            key={result.question.id}
            className={`result-card ${result.isCorrect ? 'correct' : 'incorrect'}`}
          >
            <div
              className="result-header"
              onClick={() => setExpandedQuestion(
                expandedQuestion === result.question.id ? null : result.question.id
              )}
            >
              <span className="result-icon">{result.isCorrect ? '✓' : '✗'}</span>
              <span className="result-number">Q{idx + 1}</span>
              <span className="result-id">{result.question.id}</span>
              {result.time > 0 && (
                <span className="result-time">{formatTime(result.time)}</span>
              )}
              <span className="expand-icon">
                {expandedQuestion === result.question.id ? '▼' : '▶'}
              </span>
            </div>

            {expandedQuestion === result.question.id && (
              <div className="result-details">
                <div className="result-stem">{result.question.stem}</div>

                <div className="result-options">
                  {result.question.options.map((option, optIdx) => {
                    const isCorrectAnswer = optIdx === result.question.correctAnswer;
                    const isUserAnswer = optIdx === result.userAnswer;
                    let className = 'result-option';
                    if (isCorrectAnswer) className += ' correct-answer';
                    if (isUserAnswer && !isCorrectAnswer) className += ' wrong-answer';

                    return (
                      <div key={optIdx} className={className}>
                        <span className="option-letter">{optionLetters[optIdx]}.</span>
                        <span>{option}</span>
                        {isCorrectAnswer && <span className="correct-badge">Correct</span>}
                        {isUserAnswer && !isCorrectAnswer && (
                          <span className="your-answer-badge">Your answer</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="result-rationale">
                  <strong>Explanation:</strong> {result.question.rationale}
                </div>

                {result.question.sourceExcerpt && (
                  <div className="result-source">
                    <blockquote>"{result.question.sourceExcerpt}"</blockquote>
                    <cite>— {result.question.sourceLocation}</cite>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="results-actions">
        <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
          Return to Dashboard
        </button>
        <button className="btn btn-primary" onClick={handleRetryQuiz}>
          Retry Quiz
        </button>
      </div>
    </div>
  );
}

export default QuizResults;
